import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../scripts/AuthContext";
import { useOrgFilter } from "../scripts/OrgFilterContext";
import { notifyOrgBrandChanged } from "../scripts/OrgBrandThemeProvider";
import { useConfirm } from "@/components/ConfirmDialogProvider";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import AvatarSourceEditor from "@/components/avatars/AvatarSourceEditor";
import OrgFilesSettingsPanel from "./OrgFilesSettingsPanel";
import OrgArchivePanel from "./OrgArchivePanel";
import OrgRetentionSettingsPanel from "./OrgRetentionSettingsPanel";
import OrgPlanUsagePanel from "./OrgPlanUsagePanel";
import OrgAuditLogPanel from "./OrgAuditLogPanel";
import OrgConnectorsPanel from "./OrgConnectorsPanel";
import OrgPartnerSpacesPanel from "./OrgPartnerSpacesPanel";
import OrgSsoSettingsPanel from "./OrgSsoSettingsPanel";
import {
  clearOrgAvatar,
  setOrgAvatarUrl,
  uploadOrgAvatar,
} from "@/lib/avatarApi";
import {
  CIRCA_BRAND_DEFAULTS,
  isBrandHex,
  normalizeBrandHexInput,
} from "@/lib/brandColors";
import CommunitySpaceEditForm from "./CommunitySpaceEditForm";
import CommunityMembersPanel from "./CommunityMembersPanel";
import BrandColorField from "./BrandColorField";
import VerifiedOrgMark from "./VerifiedOrgMark";
import { toastSuccess, toastError } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { API_BASE_URL } from "@/lib/apiBase";

const SECTIONS = [
  { id: "general", label: "General" },
  { id: "security", label: "Security" },
  { id: "community", label: "Community Spaces" },
  { id: "partners", label: "Partner spaces" },
  { id: "connectors", label: "Circatrons" },
  { id: "plan", label: "Plan & usage" },
  { id: "audit", label: "Audit log" },
  { id: "files", label: "Files" },
  { id: "archive", label: "Archive" },
  { id: "retention", label: "Retention" },
];

const normalizeSection = (raw, allowed = SECTIONS) => {
  if (allowed.some((s) => s.id === raw)) return raw;
  return allowed[0]?.id || "general";
};

/**
 * Full-page org settings.
 * - org.manage_settings / owners: all sections
 * - org.integrations: Circatrons section (personal bots live under Profile → My bots)
 * Route: /orgs/:orgId/settings?section=
 */
const OrgSettingsPage = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { accessToken } = useAuth();
  const confirm = useConfirm();
  const { setSelectedOrgId } = useOrgFilter();

  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [canManageSettings, setCanManageSettings] = useState(false);
  const [canIntegrations, setCanIntegrations] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [org, setOrg] = useState(null);
  const [section, setSection] = useState(() =>
    normalizeSection(searchParams.get("section"))
  );

  const visibleSections = useMemo(() => {
    if (canManageSettings) return SECTIONS;
    if (canIntegrations) {
      return SECTIONS.filter((s) => s.id === "connectors");
    }
    return [];
  }, [canManageSettings, canIntegrations]);

  const [settingsForm, setSettingsForm] = useState({
    name: "",
    description: "",
  });
  const [brandForm, setBrandForm] = useState({
    brand_primary: "",
    brand_secondary: "",
    brand_accent: "",
  });
  const [brandBusy, setBrandBusy] = useState(false);
  const [brandError, setBrandError] = useState(null);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [isDeletingOrg, setIsDeletingOrg] = useState(false);
  const [socialPresence, setSocialPresence] = useState(null);
  const [socialBusy, setSocialBusy] = useState(false);
  const [socialError, setSocialError] = useState(null);
  const [ownedCommunities, setOwnedCommunities] = useState([]);
  const [editingOwnedId, setEditingOwnedId] = useState(null);
  const [ownedEditForm, setOwnedEditForm] = useState({
    display_name: "",
    description: "",
    join_policy: "open",
    posting_policy: "official_only",
    brand_primary: "",
    brand_secondary: "",
    brand_accent: "",
    guidelines: "",
  });
  const [ownedEditBusy, setOwnedEditBusy] = useState(false);
  const [createCommunityForm, setCreateCommunityForm] = useState({
    display_name: "",
    description: "",
    join_policy: "open",
    posting_policy: "official_only",
  });
  const [createCommunityBusy, setCreateCommunityBusy] = useState(false);
  const [createCommunityError, setCreateCommunityError] = useState(null);

  axios.defaults.withCredentials = true;

  // Route → workspace only (omit selectedOrgId so switcher isn't fought mid-navigation).
  useEffect(() => {
    if (orgId) {
      setSelectedOrgId(orgId);
    }
  }, [orgId, setSelectedOrgId]);

  useEffect(() => {
    if (!visibleSections.length) return;
    const next = normalizeSection(
      searchParams.get("section"),
      visibleSections
    );
    if (next !== section) setSection(next);
  }, [searchParams, visibleSections]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!accessToken || !orgId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      setForbidden(false);
      try {
        const meRes = await axios.get(`${API_BASE_URL}/org/${orgId}/me`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const caps = new Set(
          Array.isArray(meRes.data?.capabilities)
            ? meRes.data.capabilities
            : []
        );
        const isOwner = Boolean(meRes.data?.is_owner);
        const manageSettings =
          caps.has("org.manage_settings") || isOwner;
        const integrations = caps.has("org.integrations") || isOwner;
        if (!manageSettings && !integrations) {
          if (!cancelled) setForbidden(true);
          return;
        }
        if (!cancelled) {
          setCanManageSettings(manageSettings);
          setCanIntegrations(integrations);
        }

        const orgRes = await axios.get(`${API_BASE_URL}/org/${orgId}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const orgData = orgRes.data;
        if (cancelled) return;
        setOrg({
          id: orgData.org_id,
          org_id: orgData.org_id,
          name: orgData.name,
          description: orgData.description || "",
          created_at: orgData.created_at,
          owned_by: orgData.owned_by,
          avatar_url: orgData.avatar_url || null,
          brand_primary: orgData.brand_primary || null,
          brand_secondary: orgData.brand_secondary || null,
          brand_accent: orgData.brand_accent || null,
          retention_posts_days: orgData.retention_posts_days,
          retention_cards_days: orgData.retention_cards_days,
          retention_channels_days: orgData.retention_channels_days,
        });
        setSettingsForm({
          name: orgData.name || "",
          description: orgData.description || "",
        });
        setBrandForm({
          brand_primary: orgData.brand_primary || "",
          brand_secondary: orgData.brand_secondary || "",
          brand_accent: orgData.brand_accent || "",
        });

        try {
          const presenceRes = await axios.get(
            `${API_BASE_URL}/org/${orgId}/social/presence`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          if (cancelled) return;
          const p = presenceRes.data;
          setSocialPresence(p);
          if (p?.enabled) {
            try {
              const ownedRes = await axios.get(
                `${API_BASE_URL}/org/${orgId}/social/communities`,
                { headers: { Authorization: `Bearer ${accessToken}` } }
              );
              if (!cancelled) {
                setOwnedCommunities(
                  Array.isArray(ownedRes.data?.communities)
                    ? ownedRes.data.communities
                    : []
                );
              }
            } catch {
              if (!cancelled) setOwnedCommunities([]);
            }
          } else if (!cancelled) {
            setOwnedCommunities([]);
          }
        } catch {
          if (!cancelled) {
            setSocialPresence(null);
            setOwnedCommunities([]);
          }
        }
      } catch (err) {
        console.error("Failed to load org settings:", err);
        if (!cancelled) {
          setLoadError(
            err.response?.data?.message ||
              err.message ||
              "Failed to load settings"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [accessToken, orgId]);

  useEffect(() => {
    if (forbidden && orgId) {
      navigate(`/orgs/${orgId}`, { replace: true });
    }
  }, [forbidden, orgId, navigate]);

  const handleSectionChange = (value) => {
    const next = normalizeSection(value, visibleSections);
    setSection(next);
    const params = new URLSearchParams(searchParams);
    if (next === "general" && canManageSettings) params.delete("section");
    else params.set("section", next);
    setSearchParams(params, { replace: true });
  };

  const handleSaveSettings = async () => {
    if (!accessToken || !orgId) return;
    setSettingsBusy(true);
    setSettingsError(null);
    try {
      const updated = await axios.patch(
        `${API_BASE_URL}/org/${orgId}`,
        {
          name: settingsForm.name,
          description: settingsForm.description,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setOrg((prev) =>
        prev
          ? {
              ...prev,
              name: updated.data.name,
              description: updated.data.description || "",
            }
          : prev
      );
      toastSuccess("Profile saved");
    } catch (err) {
      setSettingsError(
        err.response?.data?.message || err.message || "Failed to save settings"
      );
    } finally {
      setSettingsBusy(false);
    }
  };

  const handleSaveBrandColors = async () => {
    if (!accessToken || !orgId) return;
    const fields = ["brand_primary", "brand_secondary", "brand_accent"];
    for (const key of fields) {
      const raw = brandForm[key]?.trim();
      if (raw && !isBrandHex(raw)) {
        setBrandError(`${key.replace("brand_", "")} must be #RRGGBB`);
        return;
      }
    }
    setBrandBusy(true);
    setBrandError(null);
    try {
      const body = {
        brand_primary: normalizeBrandHexInput(brandForm.brand_primary),
        brand_secondary: normalizeBrandHexInput(brandForm.brand_secondary),
        brand_accent: normalizeBrandHexInput(brandForm.brand_accent),
      };
      const updated = await axios.patch(`${API_BASE_URL}/org/${orgId}`, body, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setOrg((prev) =>
        prev
          ? {
              ...prev,
              brand_primary: updated.data.brand_primary || null,
              brand_secondary: updated.data.brand_secondary || null,
              brand_accent: updated.data.brand_accent || null,
            }
          : prev
      );
      setBrandForm({
        brand_primary: updated.data.brand_primary || "",
        brand_secondary: updated.data.brand_secondary || "",
        brand_accent: updated.data.brand_accent || "",
      });
      notifyOrgBrandChanged();
      toastSuccess("Brand colors saved");
    } catch (err) {
      setBrandError(
        err.response?.data?.message ||
          err.message ||
          "Failed to save brand colors"
      );
    } finally {
      setBrandBusy(false);
    }
  };

  const handleRevertBrandColors = async () => {
    if (!accessToken || !orgId) return;
    const ok = await confirm({
      title: "Revert brand colors?",
      description:
        "This clears primary, secondary, and accent so the workspace uses Circa defaults again.",
      confirmLabel: "Revert to default",
      variant: "destructive",
    });
    if (!ok) return;
    setBrandBusy(true);
    setBrandError(null);
    try {
      await axios.patch(
        `${API_BASE_URL}/org/${orgId}`,
        {
          brand_primary: null,
          brand_secondary: null,
          brand_accent: null,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setOrg((prev) =>
        prev
          ? {
              ...prev,
              brand_primary: null,
              brand_secondary: null,
              brand_accent: null,
            }
          : prev
      );
      setBrandForm({
        brand_primary: "",
        brand_secondary: "",
        brand_accent: "",
      });
      notifyOrgBrandChanged();
      toastSuccess("Reverted to Circa defaults");
    } catch (err) {
      setBrandError(
        err.response?.data?.message ||
          err.message ||
          "Failed to revert brand colors"
      );
    } finally {
      setBrandBusy(false);
    }
  };

  const handleEnableSocialPresence = async () => {
    if (!accessToken || !orgId || socialBusy) return;
    setSocialBusy(true);
    setSocialError(null);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/org/${orgId}/social/presence/enable`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setSocialPresence(res.data);
      try {
        const ownedRes = await axios.get(
          `${API_BASE_URL}/org/${orgId}/social/communities`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        setOwnedCommunities(
          Array.isArray(ownedRes.data?.communities)
            ? ownedRes.data.communities
            : []
        );
      } catch {
        setOwnedCommunities(
          res.data?.community ? [res.data.community] : []
        );
      }
      toastSuccess("Community Spaces enabled");
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to enable Social presence";
      setSocialError(msg);
      toastError(msg);
    } finally {
      setSocialBusy(false);
    }
  };

  const startEditOwnedCommunity = (c) => {
    setEditingOwnedId(c.community_id);
    setOwnedEditForm({
      display_name: c.display_name || "",
      description: c.description || "",
      join_policy: c.join_policy || "open",
      posting_policy: c.posting_policy || "official_only",
      brand_primary: c.brand_primary || "",
      brand_secondary: c.brand_secondary || "",
      brand_accent: c.brand_accent || "",
      guidelines: c.guidelines || "",
    });
  };

  const handleSaveOwnedCommunity = async () => {
    if (!accessToken || !editingOwnedId || ownedEditBusy) return;
    const brandFields = ["brand_primary", "brand_secondary", "brand_accent"];
    for (const key of brandFields) {
      const raw = (ownedEditForm[key] || "").trim();
      if (raw && !isBrandHex(raw)) {
        toastError(`${key.replace("brand_", "")} must be #RRGGBB`);
        return;
      }
    }
    const name = (ownedEditForm.display_name || "").trim();
    if (!name || name.length < 2) {
      toastError("Name must be at least 2 characters");
      return;
    }
    setOwnedEditBusy(true);
    try {
      const res = await axios.patch(
        `${API_BASE_URL}/social/communities/${editingOwnedId}`,
        {
          display_name: name,
          description: ownedEditForm.description,
          join_policy: ownedEditForm.join_policy,
          posting_policy: ownedEditForm.posting_policy,
          brand_primary: normalizeBrandHexInput(ownedEditForm.brand_primary),
          brand_secondary: normalizeBrandHexInput(ownedEditForm.brand_secondary),
          brand_accent: normalizeBrandHexInput(ownedEditForm.brand_accent),
          guidelines: ownedEditForm.guidelines?.trim()
            ? ownedEditForm.guidelines.trim()
            : null,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setOwnedCommunities((prev) =>
        prev.map((c) =>
          c.community_id === editingOwnedId ? { ...c, ...res.data } : c
        )
      );
      if (
        socialPresence?.community?.community_id === editingOwnedId
      ) {
        setSocialPresence((prev) =>
          prev
            ? {
                ...prev,
                community: { ...(prev.community || {}), ...res.data },
              }
            : prev
        );
      }
      setEditingOwnedId(null);
      toastSuccess("Community updated");
    } catch (err) {
      toastError(
        err.response?.data?.message ||
          err.message ||
          "Failed to update community"
      );
    } finally {
      setOwnedEditBusy(false);
    }
  };

  const handleCreateOrgCommunity = async (e) => {
    e?.preventDefault?.();
    if (!accessToken || !orgId || createCommunityBusy) return;
    const name = createCommunityForm.display_name.trim();
    if (name.length < 2) {
      setCreateCommunityError("Name must be at least 2 characters");
      return;
    }
    setCreateCommunityBusy(true);
    setCreateCommunityError(null);
    try {
      const body = {
        display_name: name,
        join_policy: createCommunityForm.join_policy,
        posting_policy: createCommunityForm.posting_policy,
      };
      const desc = createCommunityForm.description.trim();
      if (desc) body.description = desc;

      const { data } = await axios.post(
        `${API_BASE_URL}/org/${orgId}/social/communities`,
        body,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      toastSuccess("Community created");
      const id = data?.community_id;
      if (id != null) {
        navigate(`/orgs/${encodeURIComponent(orgId)}/social/c/${id}`);
      } else {
        setOwnedCommunities((prev) => [...prev, data]);
        setCreateCommunityForm({
          display_name: "",
          description: "",
          join_policy: "open",
          posting_policy: "official_only",
        });
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to create community";
      const text = Array.isArray(msg) ? msg.join(", ") : msg;
      setCreateCommunityError(text);
      toastError(text);
    } finally {
      setCreateCommunityBusy(false);
    }
  };

  const handlePermanentDeleteOrg = async () => {
    if (!accessToken || !orgId || !org?.name || isDeletingOrg) return;
    const expected = String(org.name).trim();
    if (deleteConfirmName.trim() !== expected) {
      toastError("Type the exact organization name to confirm deletion");
      return;
    }
    const ok = await confirm({
      title: "Delete this organization forever?",
      description:
        "This cannot be undone. The workspace database, billing records, memberships, invitations, files, and notifications for this organization will be permanently removed.",
      confirmLabel: "Delete organization",
      variant: "destructive",
    });
    if (!ok) return;

    setIsDeletingOrg(true);
    try {
      await axios.delete(`${API_BASE_URL}/org/${orgId}/permanent`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        data: { confirm_name: expected },
      });
      toastSuccess("Organization permanently deleted");
      setSelectedOrgId("social");
      window.location.assign("/dashboard");
    } catch (err) {
      toastError(err, "Failed to delete organization");
      setIsDeletingOrg(false);
    }
  };

  if (loading || forbidden) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center p-6">
        <p className="text-sm text-muted-foreground">Loading settings…</p>
      </div>
    );
  }

  if (loadError || !org) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-6">
        <p className="text-sm text-destructive">
          {loadError || "Organization not found"}
        </p>
        <Button variant="outline" asChild>
          <Link to={orgId ? `/orgs/${orgId}` : "/dashboard"}>Back</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background text-foreground">
      <header className="flex min-w-0 flex-wrap items-center gap-2 border-b border-border px-3 py-2 sm:gap-3 sm:px-4 sm:py-3 md:px-6">
        <Button variant="ghost" size="sm" className="shrink-0 h-10 px-2 md:h-8" asChild>
          <Link to={`/orgs/${orgId}`}>
            <span className="md:hidden">Back</span>
            <span className="hidden md:inline">Back to {org.name}</span>
          </Link>
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-semibold tracking-tight sm:text-lg">
            Settings
          </h1>
          <p className="truncate text-sm text-muted-foreground">{org.name}</p>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-6xl min-w-0 flex-1 flex-col gap-4 p-4 sm:gap-6 sm:p-6 lg:flex-row lg:gap-10">
        <nav className="lg:w-48 lg:shrink-0" aria-label="Settings sections">
          <div className="lg:hidden">
            <Label htmlFor="org-settings-section" className="sr-only">
              Section
            </Label>
            <select
              id="org-settings-section"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={section}
              onChange={(e) => handleSectionChange(e.target.value)}
            >
              {visibleSections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <ul className="hidden space-y-1 lg:block">
            {visibleSections.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => handleSectionChange(s.id)}
                  className={cn(
                    "w-full rounded-md px-3 py-2 text-left text-sm transition-colors",
                    section === s.id
                      ? "bg-primary/10 font-medium text-primary"
                      : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                  )}
                >
                  {s.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0 flex-1">
          {section === "general" && (
            <div className="max-w-3xl space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Workspace tour</CardTitle>
                  <CardDescription>
                    Replay the guided walkthrough of teams, messages, settings,
                    and more for this organization.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      window.dispatchEvent(
                        new CustomEvent("circa-start-workspace-tour", {
                          detail: { orgId },
                        })
                      );
                    }}
                  >
                    Take the workspace tour
                  </Button>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Organization profile</CardTitle>
                  <CardDescription>
                    Logo, name, and description shown to members of this
                    workspace.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {settingsError && (
                    <Alert variant="destructive">
                      <AlertDescription>{settingsError}</AlertDescription>
                    </Alert>
                  )}
                  <AvatarSourceEditor
                    label="Organization logo"
                    currentUrl={org.avatar_url}
                    onSaveUrl={async (url) => {
                      const updated = await setOrgAvatarUrl(
                        accessToken,
                        orgId,
                        url
                      );
                      setOrg((prev) =>
                        prev
                          ? { ...prev, avatar_url: updated.avatar_url || null }
                          : prev
                      );
                    }}
                    onUploadFile={async (file) => {
                      const updated = await uploadOrgAvatar(
                        accessToken,
                        orgId,
                        file
                      );
                      setOrg((prev) =>
                        prev
                          ? { ...prev, avatar_url: updated.avatar_url || null }
                          : prev
                      );
                    }}
                    onClear={async () => {
                      const updated = await clearOrgAvatar(accessToken, orgId);
                      setOrg((prev) =>
                        prev
                          ? { ...prev, avatar_url: updated.avatar_url || null }
                          : prev
                      );
                    }}
                  />
                  <div>
                    <Label htmlFor="org-settings-name">Name</Label>
                    <Input
                      id="org-settings-name"
                      value={settingsForm.name}
                      onChange={(e) =>
                        setSettingsForm((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="org-settings-description">Description</Label>
                    <textarea
                      id="org-settings-description"
                      className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={settingsForm.description}
                      onChange={(e) =>
                        setSettingsForm((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <Button onClick={handleSaveSettings} disabled={settingsBusy}>
                    {settingsBusy ? "Saving…" : "Save profile"}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Brand colors</CardTitle>
                  <CardDescription>
                    Make this workspace feel like your company. These colors tint
                    the sidebar, page, cards, and team board; drive primary and
                    secondary buttons and tags; and add a soft accent wash behind
                    the main shell. Members who prefer Circa defaults can turn
                    branding off in Profile for this workspace only. Leave a field
                    empty to keep Circa&apos;s default. Revert clears all three.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {brandError && (
                    <Alert variant="destructive">
                      <AlertDescription>{brandError}</AlertDescription>
                    </Alert>
                  )}
                  {[
                    {
                      key: "brand_primary",
                      label: "Primary",
                      hint: "Main brand: CTAs, links, focus rings, sidebar active items, and Kanban lane accents. Also seeds the tint for sidebar, page, and cards.",
                    },
                    {
                      key: "brand_secondary",
                      label: "Secondary",
                      hint: "Supporting brand: secondary buttons, badges, and card tags; sidebar hover wash. Used as the chrome tint seed if Primary is empty.",
                    },
                    {
                      key: "brand_accent",
                      label: "Accent",
                      hint: "Decorative only: soft orbit wash behind the main workspace. Does not recolor page chrome by itself.",
                    },
                  ].map(({ key, label, hint }) => {
                    const hex = brandForm[key] || "";
                    const circaDefault = CIRCA_BRAND_DEFAULTS[key];
                    return (
                      <div key={key} className="space-y-2">
                        <Label htmlFor={`org-${key}`}>{label}</Label>
                        <p className="text-xs text-muted-foreground">{hint}</p>
                        <p className="font-mono text-xs text-muted-foreground">
                          Circa default: {circaDefault}
                        </p>
                        <BrandColorField
                          id={`org-${key}`}
                          label={label}
                          value={hex}
                          placeholderKey={key}
                          placeholder={circaDefault}
                          disabled={brandBusy}
                          onChange={(next) =>
                            setBrandForm((prev) => ({
                              ...prev,
                              [key]: next,
                            }))
                          }
                        />
                      </div>
                    );
                  })}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      onClick={handleSaveBrandColors}
                      disabled={brandBusy}
                    >
                      {brandBusy ? "Saving…" : "Save colors"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={brandBusy}
                      onClick={handleRevertBrandColors}
                    >
                      Revert to Circa default
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-destructive/40">
                <CardHeader>
                  <CardTitle className="text-destructive">Danger zone</CardTitle>
                  <CardDescription>
                    Permanently delete this organization and all of its data.
                    This cannot be undone.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Deletes the tenant database, billing checkout sessions, plan
                    usage, memberships, invitations, notifications, social
                    presence, and stored files for this organization.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="delete-org-confirm">
                      Type{" "}
                      <span className="font-medium text-foreground">
                        {org.name}
                      </span>{" "}
                      to confirm
                    </Label>
                    <Input
                      id="delete-org-confirm"
                      value={deleteConfirmName}
                      onChange={(e) => setDeleteConfirmName(e.target.value)}
                      placeholder={org.name}
                      autoComplete="off"
                      disabled={isDeletingOrg}
                    />
                  </div>
                  <Button
                    variant="destructive"
                    className="w-full sm:w-auto"
                    disabled={
                      isDeletingOrg ||
                      deleteConfirmName.trim() !== String(org.name).trim()
                    }
                    onClick={handlePermanentDeleteOrg}
                  >
                    {isDeletingOrg
                      ? "Deleting…"
                      : "Delete organization permanently"}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {section === "community" && (
            <div className="space-y-6">
              {!socialPresence?.enabled ? (
                <Card>
                  <CardHeader>
                    <CardTitle>Community Spaces</CardTitle>
                    <CardDescription>
                      Enable Circa Social presence to manage this
                      organization&apos;s public community spaces. Official
                      posts require the Social publish plan module and{" "}
                      <code className="text-xs">org.social_publish</code>.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {socialError ? (
                      <Alert variant="destructive">
                        <AlertDescription>{socialError}</AlertDescription>
                      </Alert>
                    ) : null}
                    <p className="text-sm text-muted-foreground">
                      Status:{" "}
                      <span className="font-medium text-foreground">
                        {socialPresence?.presence?.status || "disabled"}
                      </span>
                      {" — "}
                      Community Spaces stay hidden in the left nav until you
                      enable presence.
                    </p>
                    {socialPresence?.entitlements?.social_publish === false ? (
                      <p className="text-sm text-amber-700 dark:text-amber-400">
                        Your current plan does not include Social publish.
                        Upgrade to Business or Enterprise to enable Community
                        Spaces.
                      </p>
                    ) : null}
                    <Button
                      type="button"
                      disabled={
                        socialBusy ||
                        socialPresence?.entitlements?.social_publish === false
                      }
                      onClick={handleEnableSocialPresence}
                    >
                      {socialBusy ? "Enabling…" : "Enable Community Spaces"}
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>Community Spaces</CardTitle>
                    <CardDescription>
                      Primary plus up to 10 additional communities under this
                      organization. Suite staff verifies non-primary communities
                      after the org is verified. Expand a card to edit settings.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {socialError ? (
                      <Alert variant="destructive">
                        <AlertDescription>{socialError}</AlertDescription>
                      </Alert>
                    ) : null}
                    {ownedCommunities.length > 0 ? (
                      <ul className="space-y-4">
                        {ownedCommunities.map((c) => {
                          const id = c.community_id;
                          const editing = editingOwnedId === id;
                          const openHref = `/orgs/${encodeURIComponent(orgId)}/social/c/${id}`;
                          const verified = Boolean(c.is_verified);
                          return (
                            <li
                              key={id}
                              className="rounded-md border border-border p-3 space-y-3"
                            >
                              <div className="flex flex-wrap items-start justify-between gap-2 text-sm">
                                <div className="min-w-0 space-y-1">
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="font-medium text-foreground truncate">
                                      {c.display_name || `Community ${id}`}
                                    </span>
                                    <VerifiedOrgMark
                                      org={c}
                                      title="Verified community space"
                                    />
                                    {c.is_primary_for_org ? (
                                      <span className="text-xs text-muted-foreground">
                                        Primary
                                      </span>
                                    ) : null}
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    {verified ? (
                                      <span className="text-foreground/80">
                                        Verified
                                      </span>
                                    ) : (
                                      <span>Not verified</span>
                                    )}
                                    {" · "}
                                    Join: {c.join_policy || "—"} · Posting:{" "}
                                    {c.posting_policy || "—"}
                                  </p>
                                  <Link
                                    to={openHref}
                                    className="inline-block text-xs font-medium text-primary underline-offset-4 hover:underline"
                                  >
                                    Open community space
                                  </Link>
                                </div>
                                <div className="flex flex-wrap gap-2 shrink-0">
                                  <CommunityMembersPanel
                                    communityId={id}
                                    accessToken={accessToken}
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      editing
                                        ? setEditingOwnedId(null)
                                        : startEditOwnedCommunity(c)
                                    }
                                  >
                                    {editing ? "Cancel" : "Edit"}
                                  </Button>
                                </div>
                              </div>
                              {editing ? (
                                <div className="space-y-3 border-t border-border pt-3">
                                  <CommunitySpaceEditForm
                                    idPrefix={`owned-${id}`}
                                    disabled={ownedEditBusy}
                                    value={ownedEditForm}
                                    onChange={setOwnedEditForm}
                                  />
                                  <Button
                                    type="button"
                                    size="sm"
                                    disabled={ownedEditBusy}
                                    onClick={handleSaveOwnedCommunity}
                                  >
                                    {ownedEditBusy ? "Saving…" : "Save"}
                                  </Button>
                                </div>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No community spaces loaded yet.
                      </p>
                    )}

                    <form
                      onSubmit={handleCreateOrgCommunity}
                      className="space-y-4 border-t border-border pt-4"
                    >
                      <h3 className="text-sm font-medium">
                        Create community space
                      </h3>
                      {createCommunityError ? (
                        <Alert variant="destructive">
                          <AlertDescription>
                            {createCommunityError}
                          </AlertDescription>
                        </Alert>
                      ) : null}
                      <div className="space-y-2">
                        <Label htmlFor="org-community-name">Name</Label>
                        <Input
                          id="org-community-name"
                          value={createCommunityForm.display_name}
                          onChange={(e) =>
                            setCreateCommunityForm((f) => ({
                              ...f,
                              display_name: e.target.value,
                            }))
                          }
                          placeholder="Community name"
                          maxLength={200}
                          disabled={createCommunityBusy}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="org-community-description">
                          Description
                        </Label>
                        <textarea
                          id="org-community-description"
                          rows={3}
                          value={createCommunityForm.description}
                          onChange={(e) =>
                            setCreateCommunityForm((f) => ({
                              ...f,
                              description: e.target.value,
                            }))
                          }
                          placeholder="Optional"
                          maxLength={5000}
                          disabled={createCommunityBusy}
                          className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        />
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="org-community-join">Join policy</Label>
                          <select
                            id="org-community-join"
                            value={createCommunityForm.join_policy}
                            onChange={(e) =>
                              setCreateCommunityForm((f) => ({
                                ...f,
                                join_policy: e.target.value,
                              }))
                            }
                            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            disabled={createCommunityBusy}
                          >
                            <option value="open">Open</option>
                            <option value="request">Request</option>
                            <option value="invite_only">Invite only</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="org-community-posting">
                            Posting policy
                          </Label>
                          <select
                            id="org-community-posting"
                            value={createCommunityForm.posting_policy}
                            onChange={(e) =>
                              setCreateCommunityForm((f) => ({
                                ...f,
                                posting_policy: e.target.value,
                              }))
                            }
                            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            disabled={createCommunityBusy}
                          >
                            <option value="official_only">
                              Org posts only
                            </option>
                            <option value="community_posts">
                              Members can author
                            </option>
                          </select>
                        </div>
                      </div>
                      <Button
                        type="submit"
                        disabled={
                          createCommunityBusy ||
                          createCommunityForm.display_name.trim().length < 2
                        }
                      >
                        {createCommunityBusy
                          ? "Creating…"
                          : "Create community space"}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {section === "security" && (
            <OrgSsoSettingsPanel orgId={orgId} accessToken={accessToken} />
          )}

          {section === "partners" && (
            <OrgPartnerSpacesPanel orgId={orgId} accessToken={accessToken} />
          )}

          {section === "plan" && (
            <OrgPlanUsagePanel orgId={orgId} accessToken={accessToken} />
          )}

          {section === "connectors" && (
            <OrgConnectorsPanel
              orgId={orgId}
              accessToken={accessToken}
              canIntegrations={canIntegrations}
            />
          )}

          {section === "audit" && (
            <OrgAuditLogPanel orgId={orgId} accessToken={accessToken} />
          )}

          {section === "files" && (
            <OrgFilesSettingsPanel orgId={orgId} accessToken={accessToken} />
          )}

          {section === "archive" && (
            <OrgArchivePanel orgId={orgId} accessToken={accessToken} />
          )}

          {section === "retention" && (
            <OrgRetentionSettingsPanel
              orgId={orgId}
              accessToken={accessToken}
              initialOrg={org}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OrgSettingsPage;
